import { formatMoney } from '@cartiq/shared';
import type { ChatTurn, ProductCard as ProductCardData } from '../types';
import ProductCard from './ProductCard';
import CheckoutCard from './CheckoutCard';
import OrderConfirmed from './OrderConfirmed';
import AvailabilityCard from './AvailabilityCard';
import BookingCard from './BookingCard';
import Markdown from './Markdown';

interface Props {
  turn: ChatTurn;
  storeInitial: string;
  onAddToCart: (productId: string) => void;
  onViewMore: (product: ProductCardData) => void;
  /** Send a slot pick into the conversation. */
  onPick: (message: string) => void;
}

function StreamCaret() {
  return (
    <span className="ml-0.5 inline-block h-3.5 w-[2px] translate-y-0.5 animate-pulse bg-mid/70" />
  );
}

export function MessageBubble({ turn, storeInitial, onAddToCart, onViewMore, onPick }: Props) {
  const isUser = turn.role === 'user';

  // Centered system notice (handoff banner).
  if (turn.notice) {
    return (
      <div className="flex justify-center animate-fade-in-up">
        <span className="rounded-full bg-surface px-3 py-1 text-xs text-mid">{turn.text}</span>
      </div>
    );
  }

  if (isUser) {
    return (
      <div className="flex justify-end animate-fade-in-up">
        <div
          className="max-w-[75%] bg-white px-3.5 py-2.5 text-sm leading-relaxed text-primary shadow-card ring-1 ring-border"
          style={{ borderRadius: '18px 18px 4px 18px' }}
        >
          <p className="whitespace-pre-wrap break-words">{turn.text}</p>
        </div>
      </div>
    );
  }

  // Assistant turn: avatar + stacked blocks.
  const hasText = turn.blocks.some((b) => b.kind === 'text' && b.text.length > 0);

  return (
    <div className="flex items-start gap-2 animate-fade-in-up">
      <div
        className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white"
        style={{ backgroundColor: 'var(--brand-accent)' }}
      >
        {storeInitial}
      </div>

      <div className="flex min-w-0 max-w-[80%] flex-col gap-2">
        {turn.agent && (
          <span className="text-[11px] font-medium text-mid">Live agent</span>
        )}
        {turn.blocks.map((block, idx) => {
          if (block.kind === 'text') {
            if (!block.text) return null;
            return (
              <div
                key={idx}
                className={`px-3.5 py-2.5 text-sm leading-relaxed ${
                  turn.isError ? 'bg-error/10 text-error' : 'bg-accent-light text-primary'
                }`}
                style={{ borderRadius: '4px 18px 18px 18px' }}
              >
                <div className="break-words">
                  <Markdown text={block.text} />
                  {turn.streaming && idx === turn.blocks.length - 1 && <StreamCaret />}
                </div>
              </div>
            );
          }
          if (block.kind === 'products') {
            return (
              <div key={idx} className="flex flex-col gap-2">
                {block.products.map((p: ProductCardData) => (
                  <ProductCard
                    key={p.id}
                    product={p}
                    onAddToCart={onAddToCart}
                    onViewMore={onViewMore}
                  />
                ))}
              </div>
            );
          }
          if (block.kind === 'checkout') {
            return (
              <CheckoutCard
                key={idx}
                url={block.url}
                provider={block.provider}
                total={
                  block.total != null && block.currency
                    ? formatMoney(block.total, block.currency)
                    : undefined
                }
              />
            );
          }
          if (block.kind === 'order_confirmed') {
            return <OrderConfirmed key={idx} reference={block.reference} />;
          }
          if (block.kind === 'availability') {
            return (
              <AvailabilityCard
                key={idx}
                offeringName={block.offeringName}
                slots={block.slots}
                onPick={onPick}
              />
            );
          }
          if (block.kind === 'booking') {
            return (
              <BookingCard key={idx} booking={block.booking} checkoutUrl={block.checkoutUrl} />
            );
          }
          return null;
        })}

        {/* Empty streaming bubble before first chunk arrives. */}
        {turn.streaming && !hasText && (
          <div
            className="bg-accent-light px-3.5 py-2.5"
            style={{ borderRadius: '4px 18px 18px 18px' }}
          >
            <StreamCaret />
          </div>
        )}
      </div>
    </div>
  );
}

export default MessageBubble;
